/**
 * Restaurant-Settings — plattformfreier Kern (0.11).
 *
 * Beginnt mit dem OpeningHour-Typ, den tables/types.ts braucht (vorher
 * Import aus useRestaurantSettings — ein src-Pfad, den core nicht kennen
 * darf). 0.12 erweitert dieses Modul um die restlichen Settings-Typen.
 */

export type OpeningHour = {
  day: 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
  closed: boolean;
  open?: string;
  close?: string;
};

/** Ein Öffnungszeiten-Eintrag in schema.org-Form. */
export type OpeningHoursSpecification = {
  '@type': 'OpeningHoursSpecification';
  dayOfWeek: string;
  opens: string;
  closes: string;
};

const DAY_TO_SCHEMA: Record<OpeningHour['day'], string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

/**
 * Öffnungszeiten aus dem Backoffice in schema.org-Form.
 *
 * Ruhetage werden WEGGELASSEN, nicht als geschlossen markiert — so schreibt
 * schema.org es vor: `openingHoursSpecification` listet, wann offen ist.
 *
 * Einträge ohne Uhrzeit fallen ebenfalls weg. Lieber eine kürzere Liste als
 * ein erfundener Wert: Öffnungszeiten sind die Angabe, die Google im
 * Wissenspanel anzeigt, und eine falsche Stunde dort ist teurer als eine
 * fehlende. Aus demselben Grund gibt es hier bewusst KEINEN Fallback für den
 * Fall, dass noch nichts geladen ist — wer nichts weiß, sagt nichts.
 */
export function toOpeningHoursSpecification(hours: unknown): OpeningHoursSpecification[] {
  // Nimmt `unknown` mit Absicht: die Quelle ist eine jsonb-Spalte, und der
  // generierte Datenbanktyp weiß über deren Inhalt nichts. Die Prüfung gehört
  // deshalb hierher, nicht als Cast an jede Aufrufstelle.
  if (!Array.isArray(hours)) return [];
  return (hours as unknown[])
    .filter((h): h is OpeningHour & { open: string; close: string } => {
      if (typeof h !== 'object' || h === null) return false;
      const e = h as Partial<OpeningHour>;
      return (
        e.closed !== true &&
        typeof e.day === 'string' &&
        e.day in DAY_TO_SCHEMA &&
        typeof e.open === 'string' &&
        typeof e.close === 'string'
      );
    })
    .map((h) => ({
      '@type': 'OpeningHoursSpecification' as const,
      dayOfWeek: DAY_TO_SCHEMA[h.day],
      opens: h.open,
      closes: h.close,
    }));
}
